import { Link2, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CloseIcon } from "../Auth/onboarding/icons";
import { PlusIcon } from "./icons";
import { updateAssistant } from "../../actions/assistants";
import CardHeading from "./CardHeading";

// Strip a pasted URL down to the bare host the API stores.
const cleanDomain = (value) =>
  value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "");

/**
 * The assistant record carries a single `allowedDomain`, not a list, so this
 * edits that one value rather than pretending to manage several.
 */
export default function DomainSettingsCard({ assistant, onSaved }) {
  const dispatch = useDispatch();
  const assistantId = assistant?.id || "";
  const domain = assistant?.allowedDomain || "";

  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Set by the add-another-domain tile, which has nothing to open yet.
  const [soon, setSoon] = useState(false);

  // Close the form once the saved value comes back.
  useEffect(() => {
    if (domain) setAdding(false);
  }, [domain]);

  const save = async (value) => {
    if (!assistantId) return;

    setSaving(true);
    setError("");

    const result = await dispatch(
      updateAssistant(assistantId, { allowedDomain: value }),
    );

    setSaving(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't save that domain.");
      return;
    }

    setNewDomain("");
    onSaved?.();
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const trimmed = cleanDomain(newDomain);
    if (!trimmed) return;
    save(trimmed);
  };

  return (
    <section className="dash-card">
      <CardHeading
        icon={Link2}
        tone="purple"
        title="Domain settings"
        subtitle="Manage where Hostor is allowed to run."
      />

      <div className="ist-domain-header">
        <span className="cz-field-label">Allowed domain</span>
        {/* <button
          type="button"
          className="ist-add-domain-btn"
          onClick={() => setAdding((v) => !v)}
          disabled={!assistantId || saving}
        >
          <PlusIcon />
          {domain ? "Change domain" : "Add domain"}
        </button> */}
      </div>

      {error && <p className="auth-error">{error}</p>}

      {adding && (
        <form className="ist-add-domain-form" onSubmit={handleAdd}>
          <input
            type="text"
            placeholder="yourdomain.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            className="onboarding__btn onboarding__btn--primary"
            disabled={saving || !newDomain.trim()}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </form>
      )}

      <ul className="ist-domain-list">
        {domain ? (
          <li>
            <span>{domain}</span>
            <span className="bill-badge bill-badge--active">Allowed</span>
            <button
              type="button"
              className="ist-domain-remove"
              aria-label={`Remove ${domain}`}
              onClick={() => save("")}
              disabled={saving}
            >
              <CloseIcon />
            </button>
          </li>
        ) : (
          <li className="ist-domain-empty">
            <span>No domain set — the widget will run anywhere.</span>
          </li>
        )}
      </ul>

      {/* One domain per assistant is all the record holds today, so this says
          so when it is pressed rather than opening a form that cannot save a
          second one. */}
      <button
        type="button"
        className="ist-add-domain"
        onClick={() => setSoon(true)}
        aria-expanded={soon}
      >
        <span className="ist-add-domain__icon">
          <Plus size={20} strokeWidth={2.4} />
        </span>
        <span className="ist-add-domain__text">
          <strong>Add another domain</strong>
          <span>Allow Hostor on multiple domains</span>
        </span>
      </button>

      {soon && (
        <p className="ist-soon" role="status">
          Coming soon. For now Hostor runs on one domain per assistant - add a
          second assistant to cover another site.
        </p>
      )}

      {/* <p className="ist-upgrade-note">
        Need a custom domain? Upgrade your plan.
        <a href="#upgrade">
          Upgrade now
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </p> */}
    </section>
  );
}
